import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Save as SaveIcon,
  Print as PrintIcon,
  Preview as PreviewIcon,
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../../components/layout/Layout';
import barcodeSettingsService from '../../services/barcodeSettingsService';
import companySettingsService from '../../services/companySettingsService';
import productService from '../../services/productService';
import { getCurrentUser } from '../../services/authService';
import { toast } from 'react-hot-toast';

const MM_TO_PX = 3.78;

/** Paper size options (width mm, height mm) for preview and print */
const PAPER_SIZES = [
  { value: 'A4', label: 'A4 (210 × 297 mm)', widthMm: 210, heightMm: 297 },
  { value: 'A5', label: 'A5 (148 × 210 mm)', widthMm: 148, heightMm: 210 },
  { value: 'Letter', label: 'Letter (216 × 279 mm)', widthMm: 215.9, heightMm: 279.4 },
  { value: 'Legal', label: 'Legal (216 × 356 mm)', widthMm: 215.9, heightMm: 355.6 },
  { value: 'Roll72', label: 'Roll 72 mm (e.g. 72×10 tag)', widthMm: 72, heightMm: 297 },
  { value: 'Roll80', label: 'Roll 80 mm (printable ≤108 mm)', widthMm: 80, heightMm: 297 },
  { value: 'Roll100', label: 'Roll 100 mm (printable ≤108 mm)', widthMm: 100, heightMm: 297 },
];

const SCHEME_OPTIONS = [
  { value: '1x1', label: '1x1' },
  { value: '3x3', label: '3x3' },
  { value: '4x4', label: '4x4' },
  { value: '5x5', label: '5x5' },
];

/** Derive number of columns from scheme name (e.g. 1x1 → 1, 4x4 → 4) so layout reflects scheme. */
const schemeToColumns = (schemeName) => {
  if (!schemeName) return null;
  const m = String(schemeName).match(/^(\d+)x\d+$/i);
  return m ? Math.max(1, parseInt(m[1], 10)) : null;
};

const FIELD_OPTIONS = [
  { key: 'QR', label: 'Barcode / QR Image' },
  { key: 'COMPANY_NAME', label: 'Company Name' },
  { key: 'NAME', label: 'Product Name' },
  { key: 'SKU', label: 'SKU (Full)' },
  { key: 'SKU_LAST3', label: 'SKU (Last 4 Digits)' },
  { key: 'SUPPLIER_CODE', label: 'Supplier Code' },
  { key: 'PRODUCT_CODE', label: 'Product Code (Remark)' },
  { key: 'BARCODE', label: 'Barcode Number' },
  { key: 'MRP', label: 'MRP' },
  { key: 'SELLING_PRICE', label: 'Selling Price (DRP)' },
  { key: 'DATE_MMYY', label: 'Date (MMYY) - Updated At' },
  { key: 'STOCK_DATE_MMYY', label: 'Stock-In Date (MMYY)' },
  { key: 'HSN_CODE', label: 'HSN Code' },
  { key: 'CATEGORY', label: 'Category' },
];

const defaultSettings = {
  schemeName: '4x4',
  paperSize: 'A4',
  labelWidthMm: 39,
  labelHeightMm: 35,
  barcodeWidthMm: 25,
  barcodeHeightMm: 15,
  marginLeftMm: 0,
  marginTopMm: 1,
  horizontalGapMm: 0,
  verticalGapMm: 1,
  noOfColumns: 5,
  currency: 'Rs.',
  decimalsForPrice: 0,
  codeType: 'BARCODE',
  showBarcodeNumber: true,
  visibleFields: 'NAME,BARCODE,MRP',
  fieldLayout: null,
  fontSizePt: 8,
  fieldSettings: null,
};

const defaultFieldSetting = (fieldKey, order = 0) => ({
  fieldKey,
  show: true,
  left: 4,
  width: 35,
  top: 7,
  height: 15,
  fontSize: 8,
  alignment: 'Centre',
  order,
});

const mapFormatToSettings = (f) => ({
  ...defaultSettings,
  id: f.id,
  formatName: f.formatName,
  paperSize: f.paperSize || defaultSettings.paperSize,
  labelWidthMm: f.labelWidthMm ?? defaultSettings.labelWidthMm,
  labelHeightMm: f.labelHeightMm ?? defaultSettings.labelHeightMm,
  barcodeWidthMm: f.barcodeWidthMm ?? defaultSettings.barcodeWidthMm,
  barcodeHeightMm: f.barcodeHeightMm ?? defaultSettings.barcodeHeightMm,
  marginTopMm: f.marginTopMm ?? defaultSettings.marginTopMm,
  marginLeftMm: f.marginLeftMm ?? defaultSettings.marginLeftMm,
  verticalGapMm: f.verticalGapMm ?? defaultSettings.verticalGapMm,
  horizontalGapMm: f.horizontalGapMm ?? defaultSettings.horizontalGapMm,
  noOfColumns: f.noOfColumns != null ? f.noOfColumns : (schemeToColumns(f.schemeName) ?? defaultSettings.noOfColumns),
  schemeName: f.schemeName ?? defaultSettings.schemeName,
  codeType: f.codeType || defaultSettings.codeType,
  showBarcodeNumber: f.showBarcodeNumber !== false,
  visibleFields: f.visibleFields ?? defaultSettings.visibleFields,
  fieldLayout: f.fieldLayout ?? defaultSettings.fieldLayout,
  fieldSettings: f.fieldLayout ?? defaultSettings.fieldSettings,
  fontSizePt: f.fontSizePt ?? defaultSettings.fontSizePt,
});

const BarcodeGeneratePage = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(defaultSettings);
  const [formats, setFormats] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [activeFormatName, setActiveFormatName] = useState('');
  const [products, setProducts] = useState([]);
  const [productCopies, setProductCopies] = useState({});
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [fieldSettingsDialogOpen, setFieldSettingsDialogOpen] = useState(false);
  const [selectedFieldForSettings, setSelectedFieldForSettings] = useState(FIELD_OPTIONS[0].key);
  const [currentFieldSetting, setCurrentFieldSetting] = useState(defaultFieldSetting(FIELD_OPTIONS[0].key));
  const barcodeCanvasRefs = useRef({});
  const previewPrintRef = useRef(null);

  const loadFormatsAndSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [list, companyData] = await Promise.all([
        barcodeSettingsService.getFormats(),
        companySettingsService.getSettings().catch(() => ({})),
      ]);
      const arr = Array.isArray(list) ? list : [];
      setFormats(arr);
      const activeId = companyData.activeBarcodeFormatId != null ? Number(companyData.activeBarcodeFormatId) : null;
      if (arr.length > 0) {
        const formatToUse = activeId != null && arr.some((f) => f.id === activeId)
          ? arr.find((f) => f.id === activeId)
          : arr[0];
        setSelectedFormatId(formatToUse.id);
        setActiveFormatName(companyData.activeBarcodeFormatName || formatToUse.formatName || `Format ${formatToUse.id}`);
        const data = await barcodeSettingsService.getFormatById(formatToUse.id);
        setSettings(mapFormatToSettings(data));
      } else {
        setActiveFormatName('');
        setSelectedFormatId(null);
        setSettings(defaultSettings);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load label formats');
      toast.error('Failed to load formats');
      setFormats([]);
      setSelectedFormatId(null);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const hasLocation = user?.location?.id != null;

      if (hasLocation) {
        try {
          const locationProducts = await productService.getLocationProducts();
          const list = Array.isArray(locationProducts) ? locationProducts : [];
          const arr = list
            .filter((lp) => !lp.status || String(lp.status).toUpperCase() === 'ACTIVE')
            .map((lp) => ({
              id: lp.productId,
              name: lp.productName,
              sku: lp.sku || lp.productCode, // Pull from sku if available, fallback to productCode for backward compatibility
              productCode: lp.productCode, // Map the actual product code
              barcode: lp.barcode,
              mrp: lp.mrp,
              sellingPrice: lp.sellingPrice,
              category: lp.categoryName ? { name: lp.categoryName } : null,
              hsnCode: lp.hsnCode,
              supplierCode: lp.supplierCode, // Product's supplier code (for barcode label - per-product)
              updatedAt: lp.updatedAt,
              createdAt: lp.createdAt,
              stockInDate: lp.stockInDate, // When stock was last added (not updated on sales)
            }))
            .sort((a, b) => (b.id || 0) - (a.id || 0)); // Sort: recently added first (descending by ID)
          setProducts(arr);
          setProductCopies((prev) => {
            const next = { ...prev };
            arr.forEach((p) => {
              if (next[p.id] === undefined) next[p.id] = '';
            });
            return next;
          });
          return;
        } catch (locErr) {
          console.warn('Location products failed, falling back to all products:', locErr);
        }
      }

      const res = await productService.getAllProducts(0, 500, 'id', 'desc');
      const list = res?.content ?? res ?? [];
      const arr = (Array.isArray(list) ? list : []).filter(
        (p) => !p.status || String(p.status).toUpperCase() === 'ACTIVE'
      );
      setProducts(arr);
      setProductCopies((prev) => {
        const next = { ...prev };
        arr.forEach((p) => {
          if (next[p.id] === undefined) next[p.id] = '';
        });
        return next;
      });
    } catch (err) {
      console.error('Error loading products:', err);
      toast.error('Failed to load products');
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFormatsAndSettings();
    loadProducts();
  }, [loadFormatsAndSettings, loadProducts]);

  const handleSelectFormat = useCallback(async (formatId) => {
    if (formatId == null || formatId === '') return;
    const id = Number(formatId);
    if (Number.isNaN(id)) return;
    try {
      const data = await barcodeSettingsService.getFormatById(id);
      setSelectedFormatId(id);
      setSettings(mapFormatToSettings(data));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load format');
    }
  }, []);

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const setCopiesForProduct = (productId, copies) => {
    if (copies === '') {
      setProductCopies((prev) => ({ ...prev, [productId]: '' }));
      return;
    }
    const n = parseInt(copies, 10);
    setProductCopies((prev) => ({ ...prev, [productId]: isNaN(n) ? '' : Math.max(0, n) }));
  };

  /** Only fields stored in DB (user selected + "Show this object" ticked). No default list. */
  const getFieldSettingsList = () => {
    if (settings.fieldSettings) {
      try {
        const arr = JSON.parse(settings.fieldSettings);
        if (Array.isArray(arr)) {
          return arr
            .map((item) => ({
              ...defaultFieldSetting(item.fieldKey, item.order ?? 0),
              ...item,
              show: item.show !== false,
            }))
            .filter((f) => f.show === true);
        }
      } catch (e) { }
    }
    if (settings.fieldLayout) {
      try {
        const arr = JSON.parse(settings.fieldLayout);
        if (Array.isArray(arr) && arr.length > 0) {
          return arr
            .filter((item) => item.show !== false)
            .map((item, idx) => ({
              ...defaultFieldSetting(item.fieldKey, item.order ?? idx),
              ...item,
              show: true,
            }));
        }
      } catch (e) { }
    }
    return [];
  };

  /** For the selected field in dialog: from stored list if present, else defaults with show: false */
  const getFieldSetting = (fieldKey) => {
    const list = getFieldSettingsList();
    const found = list.find((f) => f.fieldKey === fieldKey);
    if (found) return found;
    const idx = FIELD_OPTIONS.findIndex((f) => f.key === fieldKey);
    return { ...defaultFieldSetting(fieldKey, idx >= 0 ? idx : 0), show: false };
  };

  /** Save: if "Show this object" checked, add/update this field in list; if unchecked, remove. Only stored fields are shown on label. */
  const saveFieldSettingsForCurrent = () => {
    const list = getFieldSettingsList();
    const next = { ...currentFieldSetting, fieldKey: selectedFieldForSettings };
    if (next.show === true) {
      const idx = list.findIndex((f) => f.fieldKey === selectedFieldForSettings);
      const newList = idx >= 0 ? list.map((f) => (f.fieldKey === selectedFieldForSettings ? { ...next, order: f.order } : f)) : [...list, { ...next, order: list.length }];
      updateSetting('fieldSettings', JSON.stringify(newList));
      toast.success('Field added/updated. It will show on the label.');
    } else {
      const newList = list.filter((f) => f.fieldKey !== selectedFieldForSettings);
      updateSetting('fieldSettings', newList.length ? JSON.stringify(newList) : null);
      toast.success('Field removed from label.');
    }
  };

  const openFieldSettingsDialog = () => {
    const list = getFieldSettingsList();
    const firstStored = list[0];
    const firstKey = firstStored ? firstStored.fieldKey : FIELD_OPTIONS[0].key;
    setSelectedFieldForSettings(firstKey);
    setCurrentFieldSetting(getFieldSetting(firstKey));
    setFieldSettingsDialogOpen(true);
  };

  const filteredProducts = products.filter(
    (p) =>
      !searchQuery ||
      (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.sku && p.sku?.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.barcode && p.barcode?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  /** Build expanded list first; totalLabels = its length so print count never has extras from duplicate productCopies keys (e.g. id vs "123"). */
  const expandedLabelList = (() => {
    const out = [];
    const seenIds = new Set();
    filteredProducts.forEach((p) => {
      const normId = String(p.id);
      if (seenIds.has(normId)) return;
      seenIds.add(normId);
      const copies = parseInt(productCopies[p.id], 10) || parseInt(productCopies[normId], 10) || 0;
      for (let i = 0; i < copies; i++) out.push(p);
    });
    return out;
  })();

  const totalLabels = expandedLabelList.length;

  /** Only fields stored in DB with show=true are shown on the label. Uses full field settings (font size, alignment, order). */
  const getFieldLayout = () => {
    const list = getFieldSettingsList();
    return list
      .map((item, idx) => ({
        ...item,
        fieldKey: item.fieldKey,
        show: item.show !== false,
        alignment: item.alignment || 'Centre',
        order: item.order ?? idx,
      }))
      .filter((f) => f.show)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  };

  /** Format date as MMYY (e.g. 0126 = Jan 2026, 1125 = Nov 2025) from updated_at/created_at. */
  const formatDateMMYY = (dateVal) => {
    if (dateVal == null) return '';

    // Normalise value into a JS Date, handling different backend shapes safely.
    let d = null;
    if (dateVal instanceof Date) {
      d = dateVal;
    } else if (typeof dateVal === 'string' || typeof dateVal === 'number') {
      const tmp = new Date(dateVal);
      if (!Number.isNaN(tmp.getTime())) d = tmp;
    } else if (typeof dateVal === 'object') {
      // Handle array format [year, month, day, hour, min, sec]
      if (Array.isArray(dateVal) && dateVal.length >= 3) {
        const dTmp = new Date(dateVal[0], dateVal[1] - 1, dateVal[2]);
        if (!Number.isNaN(dTmp.getTime())) d = dTmp;
      }
      // Handle possible LocalDate/LocalDateTime JSON: { year, monthValue, dayOfMonth } or { year, month, day }
      else {
        const year = typeof dateVal.year === 'number' ? dateVal.year : null;
        const monthRaw =
          typeof dateVal.monthValue === 'number'
            ? dateVal.monthValue
            : typeof dateVal.month === 'number'
              ? dateVal.month
              : null;
        const dayRaw =
          typeof dateVal.dayOfMonth === 'number'
            ? dateVal.dayOfMonth
            : typeof dateVal.day === 'number'
              ? dateVal.day
              : null;
        if (year != null && monthRaw != null && dayRaw != null) {
          const tmp = new Date(year, monthRaw - 1, dayRaw);
          if (!Number.isNaN(tmp.getTime())) d = tmp;
        }
      }
    }

    if (!d || typeof d.getTime !== 'function' || Number.isNaN(d.getTime())) return '';

    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = String(d.getFullYear()).slice(-2);
    return month + year;
  };

  const getFieldValue = (product, fieldKey) => {
    switch (fieldKey) {
      case 'COMPANY_NAME':
        return getCurrentUser()?.companyName || 'Company Name';
      case 'NAME':
        return product.name || '';
      case 'SKU':
        return product.sku || '';
      case 'SKU_LAST3': {
        // Return last 4 characters of SKU (e.g., "SKU0001" -> "0001")
        const sku = product.sku || '';
        return sku.slice(-4);
      }
      case 'BARCODE':
        return product.barcode || '';
      case 'MRP':
        return formatPrice(product.mrp);
      case 'SELLING_PRICE':
        return formatPrice(product.sellingPrice);
      case 'DATE_MMYY': {
        const dateVal = product.updatedAt ?? product.updated_at ?? product.createdAt ?? product.created_at;
        return formatDateMMYY(dateVal);
      }
      case 'STOCK_DATE_MMYY': {
        // Uses stockInDate which is ONLY updated when stock is added (not on sales)
        const stockDate = product.stockInDate ?? product.stock_in_date ?? product.createdAt ?? product.created_at;
        return formatDateMMYY(stockDate);
      }
      case 'SUPPLIER_CODE':
        // Use product's supplier code (set at product creation or updated on purchase)
        return product.supplierCode || '';
      case 'PRODUCT_CODE':
        return product.productCode || '';
      case 'HSN_CODE':
        return product.hsnCode || '';
      case 'CATEGORY':
        return product.category?.name || '';
      default:
        return '';
    }
  };

  function formatPrice(val) {
    if (val == null) return '';
    const dec = settings.decimalsForPrice ?? 0;
    let prefix = settings.currency || 'Rs.';
    if (prefix === 'Rs') prefix = 'Rs.';
    return prefix + Number(val).toFixed(dec);
  }

  const renderOneLabel = (product, labelKey, forPreview = false) => {
    const layout = getFieldLayout();
    const marginLeftMm = settings.marginLeftMm ?? 0;
    const marginTopMm = settings.marginTopMm ?? 0;
    const marginLeftPx = marginLeftMm * MM_TO_PX;
    const marginTopPx = marginTopMm * MM_TO_PX;
    const labelWidthMmVal = settings.labelWidthMm ?? 39;
    const labelHeightMmVal = settings.labelHeightMm ?? 35;
    const labelW = labelWidthMmVal * MM_TO_PX;
    const labelH = labelHeightMmVal * MM_TO_PX;
    const isSmallLabel = labelHeightMmVal <= 15;
    const barcodeWidthMmVal = Math.max(1, Number(settings.barcodeWidthMm) || 25);
    const barcodeHeightMmVal = Math.max(1, Number(settings.barcodeHeightMm) || 15);
    const barcodeWidthPx = Math.max(8, barcodeWidthMmVal * MM_TO_PX);
    const barcodeHeightPx = Math.max(8, barcodeHeightMmVal * MM_TO_PX);
    const baseFontSizePt = settings.fontSizePt ?? 8;
    const baseFontSizePx = Math.max(6, Math.min(baseFontSizePt * 1.2, labelH * 0.8));

    const qrField = layout.find((f) => f.fieldKey === 'QR');
    const showImage =
      (settings.codeType === 'QR' || settings.codeType === 'BARCODE') &&
      !!qrField &&
      qrField.show !== false;
    const textItems = layout.filter((f) => f.fieldKey !== 'QR' && f.show !== false);

    const paperSx = forPreview
      ? {
        width: `${labelWidthMmVal}mm`,
        height: `${labelHeightMmVal}mm`,
        minHeight: `${labelHeightMmVal}mm`,
        p: `${marginTopMm}mm ${marginLeftMm}mm`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isSmallLabel ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
      }
      : {
        width: labelW,
        height: labelH,
        minHeight: labelH,
        p: `${marginTopPx}px ${marginLeftPx}px`,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isSmallLabel ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        overflow: 'hidden',
        position: 'relative',
        fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
      };

    // Position barcode/QR image using its field settings (left/top/width/height in mm)
    const qrLeftMm = qrField?.left != null ? qrField.left : 0;
    const qrTopMm = qrField?.top != null ? qrField.top : 0;
    const qrWidthMm = qrField?.width != null ? qrField.width : barcodeWidthMmVal;
    const qrHeightMm = qrField?.height != null ? qrField.height : barcodeHeightMmVal;

    const barcodeBoxSx = {
      position: 'absolute',
      left: `${qrLeftMm}mm`,
      top: `${qrTopMm}mm`,
      width: `${qrWidthMm}mm`,
      height: `${qrHeightMm}mm`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    return (
      <Paper
        key={labelKey}
        variant="outlined"
        sx={paperSx}
      >
        {showImage && (
          <Box sx={barcodeBoxSx}>
            {settings.codeType === 'QR' ? (
              <QRCodeSVG
                value={product.barcode || String(product.id)}
                size={Math.min(barcodeWidthPx, barcodeHeightPx)}
                level="M"
                style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
              />
            ) : (
              <svg
                ref={(el) => {
                  if (el) barcodeCanvasRefs.current[`${labelKey}-qr`] = el;
                }}
                data-label-key={labelKey}
                data-value={product.barcode || String(product.id)}
                style={{ display: 'block', width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
              />
            )}
          </Box>

        )}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none', // let clicks pass through (though not critical for labels)
          }}
        >
          {textItems.map((item, idx) => {
            const text = getFieldValue(product, item.fieldKey);
            if (!text) return null;
            const textAlign = (item.alignment || 'Centre').toLowerCase();
            const textAlignMap = { left: 'left', centre: 'center', center: 'center', right: 'right' };
            const itemFontPt = item.fontSize ?? baseFontSizePt;
            const itemFontPx = Math.max(6, Math.min(itemFontPt * 1.2, labelH * 0.8));

            // Absolute positioning styles
            const posSx = {
              position: 'absolute',
              left: `${item.left ?? 4}mm`,
              top: `${item.top ?? 2}mm`,
              width: `${item.width ?? 35}mm`, // User defined width
              textAlign: textAlignMap[textAlign] || 'center',
              // height: `${item.height}mm`, // Optional, assume auto height or defined
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: `${itemFontPx}px`,
              fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif",
            };

            if (item.fieldKey === 'MRP' || item.fieldKey === 'mrp') {
              // Remove overflow/nowrap for MRP to allow stacking
              const { whiteSpace, overflow, textOverflow, ...mrpPosSx } = posSx;
              return (
                <Box
                  key={idx}
                  sx={{
                    ...mrpPosSx,
                    lineHeight: 1.1,
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: `${Math.max(6, itemFontPx * 0.9)}px`, display: 'block', fontWeight: 'bold', fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif" }}>
                    MRP
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: `${itemFontPx}px`, fontWeight: 'bold', fontFamily: "'snbc font 3', 'Arial Narrow', sans-serif" }}>
                    {text}
                  </Typography>
                </Box>
              );
            }
            return (
              <Box
                key={idx}
                sx={{
                  ...posSx,
                  fontWeight: 'bold',
                }}
              >
                {text}
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  };

  /** Draw barcode on all canvases (for preview and before print so print dialog shows content) */
  const drawBarcodes = useCallback(() => {
    if (settings.codeType !== 'BARCODE') return;
    const layout = getFieldLayout();
    const qrField = layout.find((f) => f.fieldKey === 'QR');
    const customHeightMm = qrField?.height != null ? qrField.height : (settings.barcodeHeightMm || 15);
    const customWidthMm = qrField?.width != null ? qrField.width : (settings.barcodeWidthMm || 25);
    const barcodeHeightPx = Math.max(20, customHeightMm * MM_TO_PX);
    
    // Choose a reasonable bar width (1.5-2.0) based on target width if possible, 
    // but keep it sharp. 2.0 is usually better for larger labels.
    const barWidth = customWidthMm > 30 ? 2.0 : 1.5;

    expandedLabelList.forEach((product, i) => {
      const labelKey = `label-${product.id}-${i}`;
      const canvas = barcodeCanvasRefs.current[`${labelKey}-qr`];
      if (!canvas) return;
      try {
        JsBarcode(canvas, product.barcode || String(product.id), {
          format: 'CODE128',
          width: barWidth,
          height: barcodeHeightPx,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {
        console.warn('JsBarcode error:', e);
      }
    });
  }, [settings.codeType, settings.barcodeHeightMm, settings.barcodeWidthMm, expandedLabelList]);


  useEffect(() => {
    if (settings.codeType !== 'BARCODE') return;
    if (!previewDialogOpen) return;
    const t = setTimeout(drawBarcodes, 150);
    return () => clearTimeout(t);
  }, [previewDialogOpen, drawBarcodes, settings.codeType]);

  const noOfColumns = Math.max(1, parseInt(settings.noOfColumns, 10) || 5);
  const labelWidthMm = settings.labelWidthMm ?? 39;
  const labelHeightMm = settings.labelHeightMm ?? 35;
  const horizontalGapMm = settings.horizontalGapMm ?? 0;
  const verticalGapMm = settings.verticalGapMm ?? 1;
  const labelWidthPx = labelWidthMm * MM_TO_PX;
  const labelHeightPx = labelHeightMm * MM_TO_PX;
  const horizontalGapPx = horizontalGapMm * MM_TO_PX;
  const verticalGapPx = verticalGapMm * MM_TO_PX;

  const paperSizeKey = settings.paperSize || 'A4';
  const paperSizeDef = PAPER_SIZES.find((p) => p.value === paperSizeKey) || PAPER_SIZES[0];
  const paperWidthMm = paperSizeDef.widthMm;
  const paperHeightMm = paperSizeDef.heightMm;
  const marginLeftMm = Number(settings.marginLeftMm) || 0;
  const marginTopMm = Number(settings.marginTopMm) || 0;
  const marginRightMm = marginLeftMm;
  const marginBottomMm = marginTopMm;

  // For roll/single-column printers: print area height = only the labels we're printing,
  // so the printer advances exactly that many labels (not a full page).
  const numLabels = expandedLabelList.length;
  const numRows = numLabels > 0 ? Math.ceil(numLabels / noOfColumns) : 0;
  const printSheetHeightMm =
    numRows > 0
      ? marginTopMm + numRows * labelHeightMm + Math.max(0, numRows - 1) * verticalGapMm + marginBottomMm
      : paperHeightMm;

  /** 1x1 / single column = one label per print "page" (roll printer). Browser must not use A4 pagination. */
  const oneLabelPerPage = noOfColumns === 1;

  const handlePrintPreview = () => {
    drawBarcodes();
    setTimeout(() => window.print(), 350);
  };

  /** Build standalone HTML and open in new window so browser print shows content (like test HTML). */
  const openPrintWindow = (autoPrint = false) => {
    const layout = getFieldLayout();
    const textItems = layout.filter((f) => f.fieldKey !== 'QR' && f.show !== false);
    const qrField = layout.find((f) => f.fieldKey === 'QR' && f.show !== false);
    const listToPrint = expandedLabelList.slice(0, totalLabels);
    const isBarcode = (settings.codeType || 'BARCODE') === 'BARCODE';
    const barcodeWpx = Math.max(60, (Number(settings.barcodeWidthMm) || 10) * MM_TO_PX);
    const barcodeHpx = Math.max(20, (Number(settings.barcodeHeightMm) || 10) * MM_TO_PX);

    const labels = listToPrint.map((product) => {
      const fields = textItems
        .map((item) => {
          const text = getFieldValue(product, item.fieldKey);
          if (!text) return null;
          const alignKey = (item.alignment || 'Centre').toLowerCase();
          const alignCss = alignKey === 'left' ? 'left' : alignKey === 'right' ? 'right' : 'center';
          return {
            key: item.fieldKey,
            text,
            fontSizePt: item.fontSize ?? settings.fontSizePt ?? 8,
            align: alignCss,
            left: item.left,
            top: item.top,
            width: item.width,
          };
        })
        .filter(Boolean);
      const barcodeValue = product.barcode || String(product.id);
      return {
        barcode: barcodeValue,
        qrValue: barcodeValue,
        fields,
      };

    });
    const requestedCount = Math.max(0, totalLabels);
    const numLabelsBlob = listToPrint.length;
    const numRowsBlob = numLabelsBlob > 0 ? Math.ceil(numLabelsBlob / noOfColumns) : 0;
    const printSheetHeightMmBlob =
      numRowsBlob > 0
        ? marginTopMm + numRowsBlob * labelHeightMm + Math.max(0, numRowsBlob - 1) * verticalGapMm + marginBottomMm
        : paperHeightMm;
    const oneLabelPerPageBlob = noOfColumns === 1;

    const config = {
      codeType: settings.codeType || 'BARCODE',
      labelWidthMm: Number(labelWidthMm) || 72,
      labelHeightMm: Number(labelHeightMm) || 10,
      currency: settings.currency || 'Rs.',
      baseFontSizePt: settings.fontSizePt ?? 8,
      barcodeWidthMm: Number(settings.barcodeWidthMm) || 10,
      barcodeHeightMm: Number(settings.barcodeHeightMm) || 10,
      marginTopMm: Number(settings.marginTopMm) || 0,
      marginLeftMm: Number(settings.marginLeftMm) || 0,
      marginRightMm: marginRightMm,
      marginBottomMm: marginBottomMm,
      // Grid layout: match label settings "No of columns" so blob preview shows same columns per row
      noOfColumns: noOfColumns,
      horizontalGapMm: horizontalGapMm,
      verticalGapMm: verticalGapMm,
      paperWidthMm: paperWidthMm,
      paperHeightMm: paperHeightMm,
      printSheetHeightMm: printSheetHeightMmBlob,
      numRows: numRowsBlob,
      oneLabelPerPage: oneLabelPerPageBlob,
      // Barcode / QR image field layout (in mm) for print blob; only show when "Show this object" is checked
      showBarcodeImage: !!qrField,
      qrLeftMm: qrField && typeof qrField.left === 'number' ? qrField.left : 0,
      qrTopMm: qrField && typeof qrField.top === 'number' ? qrField.top : 0,
      qrWidthMm:
        qrField && typeof qrField.width === 'number'
          ? qrField.width
          : Number(settings.barcodeWidthMm) || 10,
      qrHeightMm:
        qrField && typeof qrField.height === 'number'
          ? qrField.height
          : Number(settings.barcodeHeightMm) || 10,
    };
    const dataStr = JSON.stringify({ config, labels, requestedCount, autoPrint }).replace(/<\/script>/gi, '<\\/script>');
    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Barcode Labels – Print</title>
<script src="https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js"></script>
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
<style>
* { box-sizing: border-box; margin: 0; padding: 0; }
body { margin: 0; padding: 8px; font-family: 'snbc font 3', 'Arial Narrow', sans-serif; }
#labels { box-sizing: border-box; }
/* Multi-column grid: match label settings "No of columns" (e.g. 4 or 5 columns per row) */
#labels.labels-grid {
  display: grid;
  grid-template-columns: repeat(${config.noOfColumns}, ${config.labelWidthMm}mm);
  grid-template-rows: repeat(${config.numRows}, ${config.labelHeightMm}mm);
  gap: ${config.verticalGapMm}mm ${config.horizontalGapMm}mm;
  padding: ${config.marginTopMm}mm ${config.marginRightMm}mm ${config.marginBottomMm}mm ${config.marginLeftMm}mm;
  width: ${config.paperWidthMm}mm;
  min-height: ${config.printSheetHeightMm}mm;
  justify-content: start;
  align-content: start;
}
#labels.labels-grid .label { margin-bottom: 0 !important; page-break-after: auto !important; }
.label {
  width: ${config.labelWidthMm}mm;
  height: ${config.labelHeightMm}mm;
  position: relative;
  display: flex;
  border: 1px dashed #ccc;
  padding: ${config.marginTopMm}mm ${config.marginLeftMm}mm;
  margin-bottom: 2mm;
  page-break-after: always;
  font-family: 'snbc font 3', 'Arial Narrow', sans-serif;
}
.label:last-child { page-break-after: auto; margin-bottom: 0; }
.content { display: flex; flex: 1; min-width: 0; align-items: center; }
.left {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-right: 2mm;
}
.right {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.2;
  min-width: 0;
}
.qr { 
  flex-shrink: 0;
  display: inline-block;
  position: relative;
  width: ${Math.min(config.barcodeWidthMm, config.barcodeHeightMm)}mm;
  height: ${Math.min(config.barcodeWidthMm, config.barcodeHeightMm)}mm;
}
.qr svg {
  display: block;
  width: 100%;
  height: 100%;
}
.barcode-wrap { 
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${config.barcodeWidthMm}mm;
  height: ${config.barcodeHeightMm}mm;
}
.barcode-wrap svg { width: 100% !important; height: 100% !important; display: block !important; }
.barcode { width: 100%; height: 100%; }
.details { word-break: break-all; }
.tail { width: 22mm; flex-shrink: 0; }
@media print {
  body { margin: 0 !important; padding: 0 !important; }
  .label { border: none !important; margin-bottom: 0 !important; }
  .label:last-child { page-break-after: auto !important; }
  #labels.labels-grid {
    width: ${config.paperWidthMm}mm !important;
    height: ${config.printSheetHeightMm}mm !important;
    min-height: ${config.printSheetHeightMm}mm !important;
  }
  @page { size: ${config.oneLabelPerPage ? config.labelWidthMm + 'mm ' + config.labelHeightMm + 'mm' : config.paperWidthMm + 'mm ' + config.printSheetHeightMm + 'mm'}; margin: 0; }
}
</style>
</head>
<body>
<div id="labels"></div>
<script>
window.__LABEL_DATA__ = ${dataStr};
(function() {
  var data = window.__LABEL_DATA__;
  if (!data || !data.labels || !data.labels.length) return;
  var config = data.config;
  var allLabels = data.labels;
  var requestedCount = typeof data.requestedCount === 'number' ? data.requestedCount : allLabels.length;
  var labels = allLabels.slice(0, requestedCount);
  var container = document.getElementById('labels');
  container.className = config.oneLabelPerPage ? 'labels-stack' : 'labels-grid';

  function buildLabels() {
  for (var i = 0; i < labels.length; i++) {
    var L = labels[i];
    var labelDiv = document.createElement('div');
    labelDiv.className = 'label';

    // Barcode / QR image container only when "Show this object" is checked for Barcode/QR field
    if (config.showBarcodeImage) {
      var imgDiv = document.createElement('div');
      imgDiv.id = 'img-' + i;
      imgDiv.className = config.codeType === 'QR' ? 'qr' : 'barcode-wrap';
      imgDiv.style.position = 'absolute';
      imgDiv.style.left = (typeof config.qrLeftMm === 'number' ? config.qrLeftMm : 0) + 'mm';
      imgDiv.style.top = (typeof config.qrTopMm === 'number' ? config.qrTopMm : 0) + 'mm';
      imgDiv.style.width = (typeof config.qrWidthMm === 'number' ? config.qrWidthMm : config.barcodeWidthMm) + 'mm';
      imgDiv.style.height = (typeof config.qrHeightMm === 'number' ? config.qrHeightMm : config.barcodeHeightMm) + 'mm';

      if (config.codeType === 'BARCODE') {
        var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.id = 'bc-' + i;
        svg.className = 'barcode';
        svg.style.width = '100%';
        svg.style.height = '100%';
        imgDiv.appendChild(svg);
      }

      labelDiv.appendChild(imgDiv);
    }


    var fields = Array.isArray(L.fields) ? L.fields : [];
    for (var t = 0; t < fields.length; t++) {
      var f = fields[t];
      var line = document.createElement('div');
      
      // Absolute positioning
      line.style.position = 'absolute';
      line.style.left = (f.left !== undefined ? f.left : 4) + 'mm';
      line.style.top = (f.top !== undefined ? f.top : 2) + 'mm';
      if (f.width) line.style.width = f.width + 'mm';
      line.style.fontFamily = "'snbc font 3', 'Arial Narrow', sans-serif";

      // font size in px ≈ pt * 1.2, with a reasonable min
      var pt = typeof f.fontSizePt === 'number' ? f.fontSizePt : (config.baseFontSizePt || 8);
      var px = Math.max(6, Math.min(pt * 1.2, 32));

      if (f.key === 'MRP' || f.key === 'mrp') {
        line.style.lineHeight = '1.1';
        
        var title = document.createElement('div');
        title.textContent = 'MRP';
        title.style.fontSize = Math.max(6, px * 0.9) + 'px';
        title.style.fontWeight = 'bold';
        if (f.align) title.style.textAlign = f.align;
        line.appendChild(title);

        var val = document.createElement('div');
        val.textContent = f.text;
        val.style.fontSize = px + 'px';
        val.style.fontWeight = 'bold';
        if (f.align) val.style.textAlign = f.align;
        line.appendChild(val);
      } else {
        line.textContent = f.text;
        line.style.fontSize = px + 'px';
        if (f.align) line.style.textAlign = f.align;
        line.style.fontWeight = 'bold';
      }
      
      labelDiv.appendChild(line);
    }
    container.appendChild(labelDiv);
  }
  setTimeout(function() {
    if (!config.showBarcodeImage) return;
    for (var j = 0; j < labels.length; j++) {
      var L = labels[j];
      try {
        if (config.codeType === 'QR') {
          if (typeof qrcode !== 'undefined') {
            var qr = qrcode(0, 'M');
            qr.addData(L.qrValue);
            qr.make();
            var svgTag = qr.createSvgTag(6, 0);
            var el = document.getElementById('img-' + j);
            if (el) el.innerHTML = svgTag;
          }
        } else {
          if (typeof JsBarcode !== 'undefined' && L.barcode) {
            var bcEl = document.getElementById('bc-' + j);
            if (bcEl) {
              var barH = Math.max(20, (config.qrHeightMm || config.barcodeHeightMm) * 3.78);
              var barW = (config.qrWidthMm || config.barcodeWidthMm) > 30 ? 2.0 : 1.5;
              JsBarcode('#bc-' + j, String(L.barcode), {
                format: 'CODE128',
                width: barW,
                height: barH,
                displayValue: false,
                margin: 0
              });
            }
          }


        }
      } catch (e) { console.warn('Label render error:', e); }
    }
  }, 100);
  }

  function doPrint() {
    buildLabels();
    if (data.autoPrint) {
      setTimeout(function() {
        window.print();
        window.close();
      }, 500);
    }
  }
  if (document.readyState === 'complete') {
    doPrint();
  } else {
    window.addEventListener('load', doPrint);
  }
})();
</script>
</body>
</html>`;
    try {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'noopener,noreferrer,width=800,height=600,scrollbars=yes');

      // Reset "No Of Copies" fields immediately - whether popup opened or was blocked.
      // Print data was already captured in the blob, so reset is safe. User must allow popups and re-enter if blocked.
      setProductCopies({});

      if (w) {
        w.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } else {
        URL.revokeObjectURL(url);
        toast.error('Popup blocked. Allow popups for this site and try again.');
      }
    } catch (e) {
      console.error('Open print window failed', e);
      toast.error('Could not open print window.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button component={RouterLink} to="/inventory/products" startIcon={<ArrowBackIcon />} size="small">
            Back
          </Button>
          <Typography variant="h5" fontWeight="bold">
            Barcode Printing
          </Typography>
          {formats.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 200, ml: 2 }}>
              <InputLabel>Label format</InputLabel>
              <Select
                value={selectedFormatId ?? ''}
                label="Label format"
                onChange={(e) => handleSelectFormat(e.target.value)}
              >
                {formats.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.formatName || `Format ${f.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && activeFormatName && formats.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Label format in use: <strong>{activeFormatName}</strong>. Change above to use a different format for this session.
          </Typography>
        )}
        {!loading && formats.length === 0 && !error && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No label formats found. Create one in Settings → Label settings (Barcode / QR), then set it as &quot;Use it&quot; so it appears here for printing.
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Left: Product list with No Of Copies */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                Product list
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, SKU, barcode..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 1 }}
              />
              <TableContainer sx={{ maxHeight: 440, overflow: 'auto', mb: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Stock Name</TableCell>
                      <TableCell>BARCODE</TableCell>
                      <TableCell>MRP</TableCell>
                      <TableCell align="center" width={120}>
                        No Of Copies
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProducts.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.name}</TableCell>
                          <TableCell>{p.barcode || '-'}</TableCell>
                          <TableCell>{formatPrice(p.mrp)}</TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={productCopies[p.id] === 0 ? '' : (productCopies[p.id] ?? '')}
                              onChange={(e) => setCopiesForProduct(p.id, e.target.value)}
                              onWheel={(e) => e.target.blur()}
                              placeholder=""
                              inputProps={{
                                min: 0,
                                style: { textAlign: 'center' }
                              }}
                              sx={{
                                width: 80,
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                  display: 'none',
                                },
                                '& input[type=number]': {
                                  MozAppearance: 'textfield',
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total labels selected: <strong>{totalLabels}</strong>
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SettingsIcon />}
                    component={RouterLink}
                    to="/settings/label-settings"
                  >
                    Label settings
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={() => openPrintWindow(false)}
                    disabled={totalLabels === 0}
                  >
                    Show preview (opens in new window for print)
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PrintIcon />}
                    onClick={() => openPrintWindow(true)}
                    disabled={totalLabels === 0}
                  >
                    Direct Print
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Preview dialog: A4 sheet, 5 per row */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh' } }}
      >
        <DialogTitle>
          Preview – Barcode labels ({oneLabelPerPage ? `Roll ${labelWidthMm}×${labelHeightMm} mm` : paperSizeKey})
        </DialogTitle>
        <DialogContent dividers ref={previewPrintRef} sx={{ padding: 0, overflow: 'auto', display: 'flex', justifyContent: 'flex-start', position: 'relative' }}>
          {numLabels > 0 && (
            <Typography variant="caption" className="barcode-preview-copy-info" sx={{ position: 'absolute', top: 8, right: 16, zIndex: 1 }}>
              {oneLabelPerPage
                ? `Printing exactly ${numLabels} labels (1 per page, ${labelWidthMm}×${labelHeightMm} mm)`
                : `Printing exactly ${numLabels} labels (${noOfColumns}×${numRows} layout)`}
            </Typography>
          )}
          <Box
            className="barcode-preview-sheet"
            data-one-label-per-page={oneLabelPerPage}
            data-paper-width-mm={paperWidthMm}
            data-paper-height-mm={paperHeightMm}
            data-print-height-mm={printSheetHeightMm}
            data-num-labels={numLabels}
            data-num-rows={numRows}
            data-num-columns={noOfColumns}
            data-label-width-mm={labelWidthMm}
            data-label-height-mm={labelHeightMm}
            sx={{
              width: oneLabelPerPage ? `${labelWidthMm}mm` : `${paperWidthMm}mm`,
              minHeight: oneLabelPerPage ? undefined : `${paperHeightMm}mm`,
              padding: 0,
              margin: 0,
              bgcolor: '#fff',
              boxSizing: 'border-box',
              '@media print': oneLabelPerPage
                ? { margin: 0, padding: 0, boxShadow: 'none', bgcolor: '#fff', position: 'relative' }
                : {
                  width: `${paperWidthMm}mm`,
                  height: `${printSheetHeightMm}mm`,
                  minHeight: `${printSheetHeightMm}mm`,
                  maxHeight: `${printSheetHeightMm}mm`,
                  margin: 0,
                  padding: 0,
                  boxShadow: 'none',
                  bgcolor: '#fff',
                },
            }}
          >
            <Box
              className="barcode-preview-grid"
              sx={{
                display: oneLabelPerPage ? 'block' : 'grid',
                ...(oneLabelPerPage
                  ? {}
                  : {
                    gridTemplateColumns: `repeat(${noOfColumns}, ${labelWidthMm}mm)`,
                    gridTemplateRows: numRows > 0 ? `repeat(${numRows}, ${labelHeightMm}mm)` : undefined,
                    gap: `${verticalGapMm}mm ${horizontalGapMm}mm`,
                    justifyContent: 'start',
                    alignContent: 'start',
                  }),
                boxSizing: 'border-box',
                paddingTop: oneLabelPerPage ? 0 : `${Number(marginTopMm) || 0}mm`,
                paddingRight: oneLabelPerPage ? 0 : `${Number(marginRightMm) || 0}mm`,
                paddingBottom: oneLabelPerPage ? 0 : `${Number(marginBottomMm) || 0}mm`,
                paddingLeft: oneLabelPerPage ? 0 : `${Number(marginLeftMm) || 0}mm`,
                margin: 0,
              }}
            >
              {expandedLabelList.map((product, i) =>
                oneLabelPerPage ? (
                  <Box
                    key={`label-${product.id}-${i}`}
                    className="barcode-label-page"
                    sx={{
                      width: `${labelWidthMm}mm`,
                      height: `${labelHeightMm}mm`,
                      minWidth: `${labelWidthMm}mm`,
                      minHeight: `${labelHeightMm}mm`,
                      boxSizing: 'border-box',
                      margin: 0,
                      padding: 0,
                      marginBottom: `${verticalGapMm}mm`,
                      '&:last-of-type': { marginBottom: 0 },
                      '@media print': {
                        marginBottom: 0,
                      },
                    }}
                  >
                    {renderOneLabel(product, `label-${product.id}-${i}`, true)}
                  </Box>
                ) : (
                  renderOneLabel(product, `label-${product.id}-${i}`, true)
                )
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setPreviewDialogOpen(false)}>
            Close
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintPreview}>
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fields settings dialog */}
      <Dialog open={fieldSettingsDialogOpen} onClose={() => setFieldSettingsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Barcode field settings</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Select field name</InputLabel>
            <Select
              value={selectedFieldForSettings}
              label="Select field name"
              onChange={(e) => {
                const key = e.target.value;
                setSelectedFieldForSettings(key);
                setCurrentFieldSetting(getFieldSetting(key));
              }}
            >
              {FIELD_OPTIONS.map((o) => (
                <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentFieldSetting.show !== false}
                onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, show: e.target.checked }))}
              />
            }
            label="Show this object"
          />
          <Typography variant="caption" display="block" sx={{ mt: 1, mb: 0.5 }}>
            Position (mm)
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Left"
                type="number"
                value={currentFieldSetting.left ?? 0}
                onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, left: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            {selectedFieldForSettings === 'QR' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Width"
                  type="number"
                  value={currentFieldSetting.width ?? 35}
                  onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, width: parseFloat(e.target.value) || 35 }))}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Top"
                type="number"
                value={currentFieldSetting.top ?? 0}
                onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, top: parseFloat(e.target.value) || 0 }))}
              />
            </Grid>
            {selectedFieldForSettings === 'QR' && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Height"
                  type="number"
                  value={currentFieldSetting.height ?? 15}
                  onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, height: parseFloat(e.target.value) || 15 }))}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Font size"
                type="number"
                value={currentFieldSetting.fontSize ?? 8}
                onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, fontSize: parseInt(e.target.value, 10) || 8 }))}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Alignment</InputLabel>
                <Select
                  value={currentFieldSetting.alignment || 'Centre'}
                  label="Alignment"
                  onChange={(e) => setCurrentFieldSetting((p) => ({ ...p, alignment: e.target.value }))}
                >
                  <MenuItem value="Left">Left</MenuItem>
                  <MenuItem value="Centre">Centre</MenuItem>
                  <MenuItem value="Right">Right</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<CloseIcon />} onClick={() => setFieldSettingsDialogOpen(false)}>
            Close
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveFieldSettingsForCurrent}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @media print {
          body { margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden; }
          .barcode-preview-copy-info { visibility: hidden !important; }
          .barcode-preview-sheet,
          .barcode-preview-sheet *,
          .barcode-preview-sheet canvas,
          .barcode-preview-sheet svg,
          .barcode-preview-sheet .MuiPaper-root,
          .barcode-preview-sheet .MuiPaper-root * { visibility: visible !important; }
          .barcode-preview-sheet {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          ${oneLabelPerPage ? `
          /* Roll / 1x1: one label per page — no A4, no grid stacking */
          .barcode-preview-sheet {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: auto !important;
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            display: block !important;
            overflow: visible !important;
          }
          .barcode-preview-grid {
            display: block !important;
          }
          .barcode-label-page {
            width: ${labelWidthMm}mm !important;
            height: ${labelHeightMm}mm !important;
            min-width: ${labelWidthMm}mm !important;
            min-height: ${labelHeightMm}mm !important;
            max-width: ${labelWidthMm}mm !important;
            max-height: ${labelHeightMm}mm !important;
            display: block !important;
            page-break-after: always !important;
            overflow: hidden !important;
            box-sizing: border-box !important;
          }
          .barcode-label-page:last-of-type {
            page-break-after: auto !important;
          }
          .barcode-label-page .MuiPaper-root {
            width: 100% !important;
            height: 100% !important;
            min-width: 100% !important;
            min-height: 100% !important;
            flex-direction: column !important;
            background: #fff !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .barcode-preview-sheet canvas,
          .barcode-preview-sheet svg { opacity: 1 !important; }
          @page {
            size: ${labelWidthMm}mm ${labelHeightMm}mm;
            margin: 0;
          }
          ` : `
          /* Sheet (multi-column): one page with grid */
          .barcode-preview-sheet {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: ${paperWidthMm}mm !important;
            height: ${printSheetHeightMm}mm !important;
            min-height: ${printSheetHeightMm}mm !important;
            max-height: ${printSheetHeightMm}mm !important;
            overflow: hidden !important;
          }
          .barcode-preview-grid {
            display: grid !important;
            grid-template-columns: repeat(${noOfColumns}, ${labelWidthMm}mm) !important;
            ${numRows > 0 ? `grid-template-rows: repeat(${numRows}, ${labelHeightMm}mm) !important;` : ''}
          }
          .barcode-preview-sheet .MuiPaper-root {
            flex-direction: column !important;
          }
          @page {
            size: ${paperWidthMm}mm ${printSheetHeightMm}mm;
            margin: 0;
          }
          `}
        }
      `}</style>
    </Layout>
  );
};

export default BarcodeGeneratePage;
